// lib
import { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'

// components
import { RadioButton } from 'primereact/radiobutton'

// styles
import style from '@aytm/app/stats/page/sidebar/view-options/styles/ViewOptionsPanel.module.scss'

const CHART_MODES = {
  auto: { text: 'Auto' },
  pie: { icon: 'pie-chart-v2', text: 'Pie Chart' },
  columns: { icon: 'columns-chart-v2', text: 'Column Chart', charts: ['respondents', 'percents'] },
  bars: { icon: 'bars-chart-v2', text: 'Bar Chart', charts: ['brespondents', 'bpercents'] },
  // the mode leaves the answers table standing, so both the mark and the name say what it keeps
  // rather than what it drops
  none: { icon: 'csv-v2', text: 'Tables only, no charts' }
}

// the family the page default sits in, and the resting place of anything unrecognised
const DEFAULT_CHART_MODE = 'columns'

// A restored report can carry a mode outside these four families -- `map` above all, retired with
// the Map View it drove. Left unresolved it reads as undefined, and every read of the mode below
// throws on it; the sidebar mounts its panels without an error boundary, so that one throw takes
// every sibling panel down with it. Anything unrecognised rests on the default family instead.
const resolveChartMode = value =>
  (Object.hasOwn(CHART_MODES, value) && value) ||
  Object.keys(CHART_MODES).find(
    mode => Object.hasOwn(CHART_MODES[mode], 'charts') && CHART_MODES[mode].charts.includes(value)
  ) ||
  DEFAULT_CHART_MODE

const ChartModes = ({ model, features, onChartModeChange }) => {
  const [chartMode, setChartMode] = useState(resolveChartMode(model.chartMode))

  const onClick = useCallback(
    mode =>
      Object.hasOwn(CHART_MODES[chartMode], 'charts') && Object.hasOwn(CHART_MODES[mode], 'charts')
        ? onChartModeChange(CHART_MODES[mode].charts[CHART_MODES[chartMode].charts.indexOf(model.chartMode)])
        : Object.hasOwn(CHART_MODES[mode], 'charts')
          ? onChartModeChange(CHART_MODES[mode].charts[0])
          : onChartModeChange(mode),
    [chartMode]
  )

  useNonInitialEffect(() => {
    setChartMode(resolveChartMode(model.chartMode))
  }, [model.chartMode])

  const disabled = features.view_options.disabled

  // The strip is a single choice, so it reads as a radio group: each glyph carries the name its
  // tooltip shows, and the keyboard picks it the way a click does.
  const onKeyDown = mode => event => {
    if ('Enter' !== event.key && ' ' !== event.key) return
    event.preventDefault()
    onClick(mode)
  }

  const renderChartMode = mode => (
    <span
      key={`chart-mode-${mode}`}
      data-testid={`stat-view-options-chart-mode-${mode}`}
      className={classNames(style['chart-mode'], style[`chart-mode-${mode}`], {
        [style['chart-mode-selected']]: mode == chartMode,
        [style['chart-mode-word']]: !CHART_MODES[mode].icon
      })}
      role="radio"
      aria-label={CHART_MODES[mode].text}
      aria-checked={mode == chartMode}
      aria-disabled={disabled || undefined}
      title={CHART_MODES[mode].text}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? null : () => onClick(mode)}
      onKeyDown={disabled ? null : onKeyDown(mode)}
    >
      {CHART_MODES[mode].icon ? <i className={`icon-aytmfd-${CHART_MODES[mode].icon}`} /> : CHART_MODES[mode].text}
    </span>
  )

  return (
    <>
      <div className={style.section}>
        <div className={`${style.row} ${style['section-note']}`}>
          Default chart type:&nbsp;<b>{CHART_MODES[chartMode].text}</b>
        </div>

        <div
          className={classNames(`${style.row} ${style['chart-modes']}`, {
            'p-disabled': disabled
          })}
          role="radiogroup"
          aria-label="Default chart type"
        >
          {Object.keys(CHART_MODES).map(mode => renderChartMode(mode))}
        </div>
      </div>

      {Object.hasOwn(CHART_MODES[chartMode], 'charts') ? (
        <div className={style.section}>
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <RadioButton
                data-testid="stat-view-options-counts"
                className="p-radiobutton-sm"
                aria-labelledby="stat-view-options-counts-label"
                value="showCounts"
                checked={CHART_MODES[chartMode].charts[0] == model.chartMode}
                disabled={features.view_options.disabled}
                onChange={
                  features.view_options.disabled ? null : () => onChartModeChange(CHART_MODES[chartMode].charts[0])
                }
              />
              <span
                id="stat-view-options-counts-label"
                data-testid="stat-view-options-counts-label"
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': features.view_options.disabled
                })}
                onClick={
                  features.view_options.disabled ? null : () => onChartModeChange(CHART_MODES[chartMode].charts[0])
                }
              >
                Use counts in charts
              </span>
            </div>
          </div>
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <RadioButton
                data-testid="stat-view-options-percentages"
                className="p-radiobutton-sm"
                aria-labelledby="stat-view-options-percentages-label"
                value="showPercentage"
                checked={CHART_MODES[chartMode].charts[1] == model.chartMode}
                disabled={features.view_options.disabled}
                onChange={
                  features.view_options.disabled ? null : () => onChartModeChange(CHART_MODES[chartMode].charts[1])
                }
              />
              <span
                id="stat-view-options-percentages-label"
                data-testid="stat-view-options-percentages-label"
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': features.view_options.disabled
                })}
                onClick={
                  features.view_options.disabled ? null : () => onChartModeChange(CHART_MODES[chartMode].charts[1])
                }
              >
                Use percentages in charts
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

ChartModes.propTypes = {
  model: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  onChartModeChange: PropTypes.func
}

ChartModes.displayName = 'ChartModes'

export default ChartModes
